import type { DetectedFileKind } from '@signalvote/shared';

interface DetectionConfirmationBannerProps {
  /** Human-readable summary the detector built from the sampled rows. */
  summary: string;
  /** The detected kind — surfaced so the test can pin the copy. */
  detectedKind: DetectedFileKind;
  /** "Yes — proceed with the matching adapter." */
  onConfirm: () => void;
  /** "No, this is something else — re-open the kind picker omitting the
   *  previously-detected option." */
  onReject: () => void;
  /**
   * The prompt above the buttons. Defaults to "Import?" for kinds that commit
   * directly. The previewable path overrides it to a look-first phrasing so the
   * confirm action doesn't read like a commit (the only real commit is the
   * later "Import these rows").
   */
  prompt?: string;
  /**
   * The confirm-button label. Defaults to "Yes, import" (the direct-commit
   * path, e.g. a bank statement). The previewable path passes a look-first
   * label ("See what we found") because confirming only runs the dry-run.
   */
  confirmLabel?: string;
  /**
   * The in-flight label. Defaults to "Importing…" (direct-commit path). The
   * previewable path passes "Inspecting…" because confirming runs the dry-run,
   * not the commit.
   */
  busyLabel?: string;
  busy?: boolean;
}

/**
 * PPR-003 — confirmation banner the inbox shows after the detector returned
 * a high-confidence guess. Matches the design-doc copy: "This looks like a
 * Numero payouts file with 8 payouts from 3/9 – 5/18. Import?"
 *
 * The summary string is built by the backend (currently generic; per-processor
 * copy lands with each adapter ticket — see implementation-notes.html). This
 * component just renders the string + accept/reject controls.
 */
export function DetectionConfirmationBanner({
  summary,
  detectedKind,
  onConfirm,
  onReject,
  prompt = 'Import?',
  confirmLabel = 'Yes, import',
  busyLabel = 'Importing…',
  busy,
}: DetectionConfirmationBannerProps) {
  return (
    <div
      role="dialog"
      aria-label="Confirm detected file type"
      data-detected-kind={detectedKind}
      className="rounded-md border border-green-300 bg-green-50 p-4 space-y-3"
    >
      <p className="text-sm text-green-900">{summary}</p>
      <p className="text-sm text-green-900">{prompt}</p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          className="rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-60"
        >
          {busy ? busyLabel : confirmLabel}
        </button>
        <button
          type="button"
          onClick={onReject}
          disabled={busy}
          className="rounded-md border border-green-300 px-3 py-1.5 text-sm text-green-900 hover:bg-green-100 disabled:opacity-60"
        >
          No, this is something else
        </button>
      </div>
    </div>
  );
}
